import React, { FC, useEffect, ReactElement, memo, useCallback, useState, useMemo } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Form, Progress } from 'antd';
import { Button } from 'shared/ui/Button';
import {
  calculateInvoicePercents,
  CreditInvoiceParams,
  Invoice,
  InvoiceItem,
  InvoiceItemType,
  useCreditInvoiceMutation,
} from 'entities/Invoice';
import { Modal } from 'shared/ui/Modal';
import { TextArea } from 'shared/ui/TextArea';
import { InputNumber } from 'shared/ui/InputNumber';
import { showNotification } from 'app/providers/NotificationsProvider';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { UserPermissions } from 'entities/User';
import { useCheckPermission } from 'shared/utils/hooks/useCheckPermission';
import { InvoiceCreditNoteType } from 'entities/CreditNote';
import { ModernSelect as Select } from 'shared/ui/ModernSelect';
import { useGetCurrencySymbol } from 'app/appState';
import { BulletsTable } from 'shared/ui/BulletsTable';
import { Checkbox } from 'shared/ui/Checkbox';
import { splitDocumentDescription } from 'shared/utils/helpers/splitDocumentDescription';
import { formatPrice } from 'shared/utils/helpers/formatPrice';

interface CreditInvoiceProps {
  invoice: Invoice;
  action: ReactElement;
}

interface CalculatedInvoiceItem extends InvoiceItem {
  status: {
    paidAmount: number;
    creditedAmount: number;
    unpaidAmount: number;
  };
}

export const CreditInvoice: FC<CreditInvoiceProps> = memo((props) => {
  const { invoice, action } = props;

  const [isOpenedModal, setOpenedModal] = useState(false);
  const [creditAll, setCreditAll] = useState(true);

  const isForbidden = useCheckPermission(UserPermissions.CREDIT_INVOICE);
  const { t } = useAppTranslation(['contracts', 'common']);
  const currencySymbol = useGetCurrencySymbol();
  const [form] = Form.useForm();

  const [creditInvoice, { isLoading }] = useCreditInvoiceMutation();
  const [invoiceItemsToCredit, setInvoiceItemsToCredit] = useState<Record<string, number>>({});

  const invoicePaidAmount = useMemo(() => {
    const paidAmount = roundNumber(
      invoice.payments.reduce((acc, payment) => {
        if (payment.creditNote) {
          return acc + roundNumber(payment.amount - payment.creditNote.creditedAmount);
        }

        return acc + payment.amount;
      }, 0),
    );

    return paidAmount;
  }, [invoice]);

  const calculatedInvoiceItems = useMemo<CalculatedInvoiceItem[]>(() => {
    let paymentsAmountLeft = invoicePaidAmount;

    const orders: Record<string, number> = {
      [InvoiceItemType.DEPOSIT]: 1,
      [InvoiceItemType.INSURANCE]: 2,
    };

    // sort to calc deposit then insurance and then rent items
    const ordered = invoice.invoiceItems
      .map((invoiceItem) => {
        const order = orders[invoiceItem.invoiceItemType] || 3;

        return { ...invoiceItem, order };
      })
      .sort((a, b) => a.order - b.order);

    const calculatedData = ordered.map((invoiceItem) => {
      const creditedAmount = roundNumber(invoiceItem.creditNoteItems.reduce((acc, creditItem) => acc + creditItem.creditedAmount, 0));

      const amountToPay = roundNumber(invoiceItem.totalPrice - creditedAmount);

      const paidAmount = Math.min(paymentsAmountLeft, amountToPay);
      paymentsAmountLeft = roundNumber(paymentsAmountLeft - paidAmount);

      const unpaidAmount = roundNumber(amountToPay - paidAmount);

      return {
        ...invoiceItem,
        status: {
          paidAmount,
          creditedAmount,
          unpaidAmount,
        },
      };
    });

    return calculatedData;
  }, [invoice.invoiceItems, invoicePaidAmount]);

  const openModal = useCallback((): void => {
    setOpenedModal(true);
  }, []);

  const closeModal = useCallback((): void => {
    setOpenedModal(false);
    setInvoiceItemsToCredit({});
    setCreditAll(true);
  }, []);

  useEffect(() => {
    if (isOpenedModal) {
      const defaultCreditValues = calculatedInvoiceItems.reduce((acc, invoiceItem) => {
        return { ...acc, [invoiceItem.invoiceItemId]: invoiceItem.status.unpaidAmount };
      }, {});

      if (creditAll) {
        setInvoiceItemsToCredit(defaultCreditValues);
      } else {
        setInvoiceItemsToCredit({});
      }
    }
  }, [calculatedInvoiceItems, isOpenedModal, creditAll]);

  useEffect(() => {
    if (invoice) {
      form.setFieldValue('creditNoteType', InvoiceCreditNoteType.DEBT_CREDIT);
    }
  }, [invoice, form]);

  const handleCreditInvoice = useCallback(
    async (data: CreditInvoiceParams): Promise<void> => {
      try {
        const creditNoteItems = Object.entries(invoiceItemsToCredit)
          .filter(([_, creditedAmount]) => creditedAmount)
          .map(([invoiceItemId, creditedAmount]) => {
            const invoiceItem = invoice.invoiceItems.find((item) => item.invoiceItemId === invoiceItemId);

            return {
              invoiceItemId,
              creditedAmount,
              creditNoteItemType: invoiceItem!.invoiceItemType,
              description: invoiceItem?.description,
            };
          });
        if (invoice.invoiceId && creditNoteItems.length) {
          await creditInvoice({ ...data, invoiceId: invoice.invoiceId, creditNoteItems }).unwrap();
          showNotification('info', t('Success', { ns: 'common' }), t('Invoice has been successfully credited'));
        }
      } catch (error: CustomAny) {
        showNotification('error', t('Error', { ns: 'common' }), t('Error when crediting invoice'));
      } finally {
        closeModal();
      }
    },
    [closeModal, creditInvoice, invoice, invoiceItemsToCredit, t],
  );

  const creditNoteTypeOptions = useMemo(
    () => [
      { label: t(InvoiceCreditNoteType.DEBT_CREDIT, { ns: 'common' }), value: InvoiceCreditNoteType.DEBT_CREDIT },
      { label: t(InvoiceCreditNoteType.SERVICE_CREDIT, { ns: 'common' }), value: InvoiceCreditNoteType.SERVICE_CREDIT },
      { label: t(InvoiceCreditNoteType.IMPAIRMENT_DEBT_CREDIT, { ns: 'common' }), value: InvoiceCreditNoteType.IMPAIRMENT_DEBT_CREDIT },
    ],
    [t],
  );

  const hasInvalidCreditAmounts = useMemo(
    () =>
      !Object.values(invoiceItemsToCredit).length ||
      calculatedInvoiceItems.some((invoiceItem) => invoiceItemsToCredit[invoiceItem.invoiceItemId] > invoiceItem.status.unpaidAmount),
    [calculatedInvoiceItems, invoiceItemsToCredit],
  );

  return (
    <>
      {React.cloneElement(action, { onClick: openModal })}

      <Modal className="max-w-[900px]" width={900} isOpen={isOpenedModal} onClose={closeModal}>
        <Form form={form} layout="vertical" onFinish={handleCreditInvoice} className="flex flex-col" disabled={isForbidden}>
          <div className="font-semibold text-xl mb-3 font-tektur">{t('Credit invoice')}</div>

          <div className="flex flex-wrap gap-10 mb-4">
            <div className="max-w-[350px] flex-1 mb-6">
              <div className="font-normal text-xl mb-5">{t('Base info')}</div>

              <div className="mb-4">
                <Checkbox
                  name="creditAll"
                  checked={creditAll}
                  onChange={(isChecked) => {
                    setCreditAll(isChecked);
                  }}
                >
                  {t('Credit all')}
                </Checkbox>
              </div>

              <Form.Item
                label={t('Credit note type')}
                name="creditNoteType"
                className="flex-1"
                rules={[
                  {
                    required: true,
                    message: t('Please select creditNoteType!'),
                  },
                ]}
              >
                <Select options={creditNoteTypeOptions} placeholder={t('Please select creditNoteType!')} bordered />
              </Form.Item>
              <Form.Item name="description">
                <TextArea placeholder={t('Add your description...')} rows={4} bordered />
              </Form.Item>
            </div>

            <div className="max-w-[500px] min-w-[300px] flex-1">
              <div className="font-normal text-xl mb-3">{t('Invoice items')}</div>

              {calculatedInvoiceItems.map((invoiceItem) => {
                const rows = [
                  {
                    label: t('Paid'),
                    value: `${formatPrice(invoiceItem.status.paidAmount)} ${currencySymbol}`,
                    emptyValueSymbol: `0 ${currencySymbol}`,
                  },
                  {
                    label: t('Credited'),
                    value: `${formatPrice(invoiceItem.status.creditedAmount)} ${currencySymbol}`,
                    emptyValueSymbol: `0 ${currencySymbol}`,
                  },
                  {
                    label: t('Debt'),
                    value: `${formatPrice(invoiceItem.status.unpaidAmount)} ${currencySymbol}`,
                    emptyValueSymbol: `0 ${currencySymbol}`,
                  },
                ];

                const percents = calculateInvoicePercents(
                  invoiceItem.totalPrice,
                  invoiceItem.status.paidAmount,
                  invoiceItem.status.creditedAmount,
                );

                const unpaidPercent = roundNumber(100 - percents.paid - percents.credited);

                const splitedDescription = splitDocumentDescription(invoiceItem.description);

                return (
                  <div className="border border-secondary rounded-lg my-2 p-4" key={invoiceItem.invoiceItemId}>
                    <div className="flex flex-col mb-2 gap-2">
                      <div className="font-semibold text-xl flex">
                        {t(invoiceItem.invoiceItemType)}:
                        <span className="font-normal text-xl px-1">
                          {formatPrice(invoiceItem.totalPrice)} {currencySymbol}
                        </span>
                      </div>
                      <div>{`${t(splitedDescription.description, { ns: 'common' })} ${splitedDescription.documentNumber}`}</div>
                    </div>
                    <div className="mb-3">
                      <Progress
                        size={['100%', 15]}
                        strokeLinecap="butt"
                        percent={percents.paid + unpaidPercent}
                        success={{ percent: percents.paid }}
                        strokeColor="var(--primaryLight)"
                        trailColor="var(--warn)"
                        showInfo={false}
                      />
                    </div>
                    <div className="flex flex-row items-end">
                      <div className="w-[50%]">
                        <BulletsTable theme="clear" rows={rows} />
                      </div>
                      <div className="w-[50%]">
                        <InputNumber
                          onChange={(value) => {
                            setInvoiceItemsToCredit((prev) => ({ ...prev, [invoiceItem.invoiceItemId]: value || 0 }));
                          }}
                          value={invoiceItemsToCredit[invoiceItem.invoiceItemId]}
                          placeholder={t('Credit amount')}
                          status={invoiceItemsToCredit[invoiceItem.invoiceItemId] > invoiceItem.status.unpaidAmount ? 'error' : undefined}
                          isDisabled={creditAll}
                          bordered
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button theme="secondary" onClick={closeModal}>
              {t('Cancel', { ns: 'common' })}
            </Button>

            <Button type="submit" isLoading={isLoading} isDisabled={isForbidden || hasInvalidCreditAmounts}>
              {t('Save', { ns: 'common' })}
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
});
